'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { apiMethods } from '@/lib/services/api';
import { getOrCreateVisitorId } from '@/lib/utils/tracking';

const LIKED_KEY = 'ms_liked_blogs';

/** Returns the set of blog ids this browser has already liked. */
function getLikedSet(): Set<string> {
    if (typeof localStorage === 'undefined') return new Set();
    try {
        return new Set(JSON.parse(localStorage.getItem(LIKED_KEY) ?? '[]') as string[]);
    } catch {
        return new Set();
    }
}

function persistLiked(set: Set<string>): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(LIKED_KEY, JSON.stringify([...set]));
}

interface LikeButtonProps {
    blogId: string;
    initialLikes: number;
    locale: string;
}

export default function LikeButton({ blogId, initialLikes, locale }: LikeButtonProps) {
    const t = useTranslations('Blog');
    const [likes, setLikes] = useState(initialLikes);
    const [liked, setLiked] = useState(false);
    const [pending, setPending] = useState(false);

    // Liked state lives client-side (anonymous visitors), so hydrate after mount.
    useEffect(() => {
        setLiked(getLikedSet().has(blogId));
    }, [blogId]);

    const toggle = async () => {
        if (pending) return;
        const next = !liked;

        // Optimistic update
        setLiked(next);
        setLikes((n) => Math.max(0, n + (next ? 1 : -1)));
        setPending(true);

        try {
            const visitorId = getOrCreateVisitorId();
            const res = await apiMethods.post<{ message: string; data: { likes: number; liked: boolean } }>(
                `/blogs/${blogId}/${next ? 'like' : 'unlike'}`,
                { visitorId },
            );

            // Reconcile with the server's authoritative count.
            setLikes(res.data.likes);
            setLiked(res.data.liked);

            const set = getLikedSet();
            if (res.data.liked) set.add(blogId);
            else set.delete(blogId);
            persistLiked(set);
        } catch {
            // Revert on failure
            setLiked(!next);
            setLikes((n) => Math.max(0, n + (next ? -1 : 1)));
        } finally {
            setPending(false);
        }
    };

    return (
        <div className="flex items-center gap-3">
            <span className="text-sm text-(--muted)">{t('likePrompt')}</span>
            <button
                type="button"
                onClick={toggle}
                disabled={pending}
                aria-pressed={liked}
                aria-label={liked ? t('liked') : t('like')}
                className={`group inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${
                    liked
                        ? 'border-primary-500/40 bg-primary-500/10 text-primary-500'
                        : 'border-(--border) text-(--text) hover:border-primary-500/40 hover:text-primary-500'
                }`}
            >
                <svg
                    className={`w-4 h-4 transition-transform group-active:scale-90 ${liked ? 'fill-primary-500 stroke-primary-500' : 'fill-none stroke-current'}`}
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span>{liked ? t('liked') : t('like')}</span>
                <span className="tabular-nums">{likes.toLocaleString(locale === 'ar' ? 'ar-EG' : 'en-US')}</span>
            </button>
        </div>
    );
}
